/**
 * A full-featured, `Field`-based form control.
 *
 * ## Fields
 * Name | Description
 * --- | ---
 * model | The model associated with the form.
 * inputViews | A mapping from field `id` to the `FormInputView` representing the field.
 * buttons | The array of `ButtonView`s in the footer area of the form. These are in the same order as the button options hashes passed to the `buttons` option.
 *
 * @module app/views/FormView
 */
import Backbone from "backbone";
import _ from "underscore";
import $ from "jquery";
import FormInputView from "core/views/FormInputView";
import ButtonView from "core/views/ButtonView";
import isMobile from "core/is-mobile";
import fieldViewFactory from "core/views/Field/FieldViewFactory";
export default Backbone.View.extend({
  tagName: "form",

  model: {},
  inputViews: {},

  events: {
    "click .save-row": function () {
      this.trigger("commit");
    },
    "click .delete-row": function () {
      this.trigger("remove");
    },
    mouseenter: function () {
      this.$(".edit select").on(
        "focus",
        _.bind(function (evt) {
          this._skipLeave = true;
          $(evt.target).one(
            "change, blur",
            _.bind(function () {
              this._skipLeave = false;
            }, this)
          );
        }, this)
      );
      _.each(this.inputViews, function (view) {
        view.$el
          .add(view.$(".edit input, .edit select, .edit textarea"))
          .width(view.$el.width());
      });
    },
    mouseleave: function () {
      if (!this._skipLeave) {
        this.edit(this._edited);
      }
    },
    "change select": "SelectedChanged",
    "change input[type=radio]": "SelectedChanged",
  },

  template: _.template('<p class="buttons"></p>'),

  /**
   * Initializes the control.
   * @param  {object} options The options hash.
   * @param {mixed} options.model The model to link with this form.
   * @param {Array} options.buttons The array of button options hashes from which to derive `ButtonView` instances to display at the bottom of the form.
   * @param {Array} options.fields The array of `Field` instances representing fields to place in this form.
   * @param {string} options.url The "action" attribute of the underlying form tag.
   */
  initialize: function (options) {
    this.options = _.defaults({}, options, {
      buttons: [],
    });
    this.buttons = [];

    if (this.options.model instanceof Backbone.Model) {
      this.model = this.options.model;
    } else {
      this.model = new Backbone.Model(this.options.model);
    }

    this.inputViews = {};

    if (isMobile) {
      var self = this;

      this.$el.on("keydown", function (e) {
        var enterKeyCode = 13;
        var code = e.keyCode || e.which;

        //Listen for the enter key. (Go/Next/Done on Mobile).
        if (code == enterKeyCode) {
          self._tab();
        }
      });
    }

    this.$el.on("keyup keypress", function (e) {
      var code = e.keyCode || e.which;

      if (code == 13) {
        e.preventDefault();

        return false;
      }
    });
    this.visiblefieldslists = "";
  },

  render: function () {
    this.clearSelection();
    var model = this.options.model;
    var fields = this.options.fields;
    this._renderRule(model, fields);
    this.$el.html(this.template(this.options));

    _.each(
      this.options.buttons,
      function (button) {
        var buttonView = new ButtonView(button);

        this.buttons.push(buttonView);
        this.$(".buttons").append(buttonView.render().$el);
      },
      this
    );

    $.each(
      this.options.fields,
      _.bind(function (index) {
        this._addField(this.options.fields[index]);
      }, this)
    );

    this.$el
      .attr("enctype", "multipart/form-data")
      .attr("action", this.options.url)
      .attr("method", "POST");
    this.clearSelection();
  },
  _renderRule: function (model, fields) {
    var visiblefields = [];
    _.each(fields, function (field) {
      if (
        field.attributes.type == "select" ||
        field.attributes.type == "radio"
      ) {
        if (model.has(field.id)) {
          var selectValue = model.attributes[field.id];
          _.each(field.attributes.values, function (f) {
            if (f.value == selectValue) {
              visiblefields.push(f.rules);
            }
          });
        }
      }
    });
    var tempvisiblefields = "";
    _.each(fields, function (field) {
      tempvisiblefields = visiblefields.toLocaleString().split(",");
      if (_.contains(tempvisiblefields, field.id)) {
        field.attributes.visible = true;
      }
    });
    this.visiblefieldslists = tempvisiblefields;
  },
  SelectedChanged: function (e) {
    this.render();
  },

  clearSelection: function () {
    var model = this.options.model;
    var fields = this.options.fields;
    var resetfields = this.visiblefieldslists;
    _.each(fields, function (field) {
      if (_.contains(resetfields, field.id)) {
        field.attributes.visible = false;
      }
    });
    this.visiblefieldslists = "";
  },

  /**
   * Adds the given field to the form.
   * @param {Field} field The `Field` model to add.
   */
  addField: function (field) {
    this.options.fields.push(field);
    this._addField(field);
  },

  /**
   * Removes a field from the form.
   * @param  {string} attribute The id (or name) of the `Field` to remove.
   */
  removeField: function (attribute) {
    var removeIndex = false;

    _.each(this.options.fields, function (field, idx) {
      if (field.attributes.name == attribute) {
        removeIndex = idx;
      }
    });

    if (removeIndex !== false) {
      this.options.fields.splice(removeIndex, 1);
      this.inputViews[attribute].$el.parent().remove();
    }
  },

  _addField: function (field) {
    var view = this._createInputView(this.model, field);

    view.render();
    this.$(".buttons").before(view.$el);
  },

  remove: function () {
    this.$el.remove();
  },

  /**
   * Propagate form values to model fields.
   * @deprecated
   * @return {Promise} A promise object which shall resolve if the propagation is successful or reject with an error.
   */
  propagate: function () {
    return $.when();
  },

  /**
   * Commits all field values and submits the form via AJAX.
   * @param  {Boolean} useModel True if the model is a Backbone model and its `save` function is to be used in lieu of a form submission.
   * @return {Promise}          A promise object which shall resolve if the submission is successful or reject if it is not.
   */
  save: function (useModel) {
    var defer = new $.Deferred();

    this.propagate().done(
      _.bind(function () {
        if (useModel) {
          this.model.save(null, {
            success: _.bind(defer.resolve, defer),
            error: _.bind(defer.reject, defer),
          });
        } else {
          this.$el.ajaxSubmit({
            success: _.bind(defer.resolve, defer),
            error: _.bind(defer.reject, defer),
            //url: this.$el.attr('action') + '?' + this.$el.formSerialize()
          });
        }
      }, this)
    );

    return defer.promise();
  },

  /**
   * Resets the form fields.
   */
  reset: function () {
    this.$el.resetForm();
  },

  _edited: false,

  /**
   * Sets a value indicating whether or not the form/model has been modified.
   * @param  {Boolean} [value:true] True if modified, otherwise false.
   */
  edit: function (value) {
    this._edited = value !== false;
  },

  /**
   * Gets a value indicating whether or not the form/model has been modified.
   * @return {Boolean} True if modified, otherwise false.
   */
  isEdited: function () {
    return this._edited;
  },

  _createInputView: function (model, field) {
    var itemView = new FormInputView({
      model: model,
      field: field,
    });

    this.model.on(
      "change:" + field.get("name"),
      function () {
        this.edit();
        this.trigger("change:" + field.get("name"), field.getValue(model));
      },
      this
    );

    this.inputViews[field.get("name")] = itemView;

    return itemView;
  },

  delegateEvents: function () {
    Backbone.View.prototype.delegateEvents.apply(this, arguments);
    _.each(this.inputViews, function (inputView) {
      inputView.delegateEvents();
    });
  },

  /**
   * Changes the focus of the current field to the next one.
   */
  _tab: function () {
    var $focusInput = _.first(this.$(":focus"));
    var $formInputs = this.$(":input");

    //Index of input will return -1 if :
    //focusInput is Null, Undefined, or not found in formInputs.
    //formInputs is empty or is not an array.
    var indexOfInput = _.indexOf($formInputs, $focusInput);

    if ($focusInput) {
      $focusInput.blur();
    }

    //Tab to the next input iff there is one.
    if (indexOfInput >= 0 && indexOfInput < $formInputs.length - 1) {
      var $nextInput = $formInputs[indexOfInput + 1];

      if ($nextInput) {
        $nextInput.focus();
      }
    }
  },
});



//////////////////
// WEBPACK FOOTER
// ./public/js/core/views/FormView.js
// module id = 67
// module chunks = 1